import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from "react";
import { request } from "./api";
import type { Session } from "./types";

interface LoginInput {
  email: string;
  password: string;
  mfa_code?: string;
}

interface LoginResponse {
  user?: Session["user"];
  expires_at?: string;
  mfa_required?: boolean;
}

interface AuthContextValue {
  session?: Session;
  loading: boolean;
  login(input: LoginInput): Promise<LoginResponse>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: ["session"],
    queryFn: () => request<Session>("/auth/me"),
    retry: false,
    staleTime: 30_000,
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      session: session.data,
      loading: session.isLoading,
      login: async (input) => {
        const result = await request<LoginResponse>("/auth/login", {
          method: "POST",
          body: JSON.stringify(input),
        });
        if (!result.mfa_required) {
          await queryClient.invalidateQueries({ queryKey: ["session"] });
        }
        return result;
      },
      logout: async () => {
        await request<void>("/auth/logout", { method: "POST" });
        queryClient.setQueryData(["session"], undefined);
      },
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ["session"] });
      },
    }),
    [queryClient, session.data, session.isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return value;
}

